import { themeVars } from '@synoptic/ui-kit/theme/vars.ts';
import { borderRadius, space } from '@synoptic/ui-kit/tokens.js';
import { typography } from '@synoptic/ui-kit/typography.js';
import { globalStyle, keyframes, style } from '@vanilla-extract/css';

export const formContainer = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s24,
  flex: 1,
});

export const formActions = style({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: space.s16,
});

export const footerContainer = style({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  gap: space.s24,
  flexWrap: 'wrap',
  alignItems: 'center',
  paddingTop: space.s8,
});

export const footerButtonsContainer = style({
  display: 'flex',
  gap: space.s16,
  height: 'fit-content',
  justifyContent: 'flex-end',
  marginLeft: 'auto',
  flexWrap: 'wrap',
});

export const testFilterButton = style({
  width: 'fit-content',
  height: 'fit-content',
});

export const testFilterDescription = style({
  ...typography.bodyMd,
  color: themeVars.container.foreground.primary,
});

export const testFilterContainer = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s16,
});

export const textareaFooter = style({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: space.s16,
  flexWrap: 'wrap',
  padding: `0px ${space.s16}px ${space.s16}px`,
});

export const testFilterStatus = style({
  display: 'flex',
  alignItems: 'center',
  gap: space.s8,
  color: themeVars.container.foreground.primary,
  flex: 1,
  minWidth: 100,
  ...typography.titleSm,
});

globalStyle(`${testFilterStatus} > svg`, {
  minWidth: 24,
});

export const textareaContainer = style({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: borderRadius.r12,
  border: `1px solid ${themeVars.controls.border.default}`,

  ':hover': {
    borderColor: themeVars.controls.border.hover,
  },

  ':focus-within': {
    borderColor: themeVars.controls.border.active,
  },
});

export const assistantTextareaContainer = style([
  textareaContainer,
  {
    margin: `${space.s12}px ${space.s24}px 0`,
  },
]);

export const textareaInput = style({
  selectors: {
    '&:is(&)': {
      outline: 'transparent',
    },
    '&:is(&):focus-within': {
      outline: 'transparent',
    },
  },
});

export const formFieldDescription = style({
  ...typography.bodySm,
  color: themeVars.container.foreground.tertiary,
  marginTop: space.s4,
});

globalStyle(`${formFieldDescription} > a`, {
  textDecoration: 'underline',
});

export const modalFooterWithMargin = style([
  {
    display: 'flex',
    gap: space.s16,
    height: 'fit-content',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: space.s32,
  },
]);

export const modalFooterWithFlexEnd = style([
  modalFooterWithMargin,
  {
    justifyContent: 'flex-end',
  },
]);

export const userMessage = style({
  ...typography.bodyMd,
  marginLeft: 'auto',
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  padding: `${space.s12}px ${space.s16}px`,
  borderRadius: borderRadius.r12,
  backgroundColor: themeVars.container.secondary.background.idle,
});

export const assistantMessageContainer = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s14,
  transition: 'background-color 0.15s',
});

export const chatContainer = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s24,
  flex: 1,
  padding: `${space.s12}px ${space.s24}px`,
  overflow: 'auto',
});

export const chatContainerWrapper = style({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
});

export const assistantModalHeader = style({
  padding: `${space.s20}px ${space.s24}px`,
  position: 'relative',
});

export const assistantModalFooter = style([
  modalFooterWithMargin,
  {
    padding: `0 ${space.s24}px`,
  },
]);

export const assistantMessageContent = style({
  ...typography.bodyMd,
  display: 'flex',
  flexDirection: 'column',
  gap: space.s8,
  wordBreak: 'break-word',
});

export const assistantLoadingMessage = style({
  ...typography.bodyMd,
  display: 'flex',
  color: themeVars.container.foreground.tertiary,
});

globalStyle(`${assistantLoadingMessage} > svg`, {
  minWidth: 16,
  color: themeVars.container.foreground.brand,
  marginRight: space.s8,
  marginTop: space.s2,
});

const dottyAnimation = keyframes({
  '0%': {
    content: '',
  },
  '25%': {
    content: '.',
  },
  '50%': {
    content: '..',
  },
  '75%': {
    content: '...',
  },
  '100%': {
    content: '',
  },
});

globalStyle(`${assistantLoadingMessage}::after`, {
  display: 'inline-block',
  animation: `${dottyAnimation} steps(1,end) 1s infinite`,
  content: '',
});

export const useThisFilterButton = style({
  width: 'fit-content',
});

export const manageFilterModal = style({
  height: 'min(866px, 100%)',
});

export const assistantModalContent = style({
  padding: `${space.s12}px 0 ${space.s32}px`,
  scrollbarGutter: 'auto',
  overflow: 'hidden',
});

export const createWithAIAssistantPlaceholder = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s8,
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
});

globalStyle(`${createWithAIAssistantPlaceholder} > svg`, {
  color: themeVars.container.foreground.brand,
  marginBottom: space.s8,
});

export const createWithAIAssistantPlaceholderTitle = style({
  ...typography.titleLg,
});

export const createWithAIAssistantPlaceholderDescription = style({
  ...typography.bodyMd,
  color: themeVars.container.foreground.secondary,
  maxWidth: 400,
  textAlign: 'center',
});

export const createWithAIAssistantContainer = style({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'auto',
  position: 'relative',
});

export const formContent = style({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: space.s24,
});

export const assistantFilterSuggestionSelected = style({
  backgroundColor: themeVars.container.prominent.background.idle,
  padding: space.s16,
  borderRadius: borderRadius.r12,
});

export const assistantFilterSuggestionSelectedLabel = style({
  ...typography.labelMd,
  color: themeVars.container.foreground.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: space.s4,
});

export const goToSelectedFilter = style({
  position: 'absolute',
  left: space.s24,
  right: space.s24,
  top: 0,
  height: 'fit-content',
  padding: `${space.s6}px ${space.s12}px`,
  zIndex: 1,
  backgroundColor: themeVars.container.prominent.background.idle,
  borderRadius: borderRadius.r12,
  width: 'auto',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: space.s16,
  cursor: 'pointer',
  transition: 'background-color 0.15s',

  ':hover': {
    backgroundColor: themeVars.badge.brand.background.hover,
  },
});

export const goToSelectedFilterBelowViewport = style({
  bottom: 0,
  top: 'unset',
});

export const goToSelectedFilterTitle = style({
  ...typography.labelMd,
  color: themeVars.container.foreground.primary,
});

export const goToSelectedFilterDescription = style({
  ...typography.bodySm,
  color: themeVars.container.foreground.secondary,
});
